import { createSketchWallGeometryContext } from '../../plugin-sdk/sketch_wall_geometry.js';
import { PLAN_LAYER_IDS as L, floorplanPlanLayerId, isPlanLayerVisible } from './plan_layer_service.js';

export const COMMON_PLAN_OWNER = 'core_plan_content';
const validPoint = p => p && Number.isFinite(p.x) && Number.isFinite(p.z);
const positive = n => Number.isFinite(n) && n > 0;
const copyPoint = p => ({ x: p.x, z: p.z });
const orders = new Map([[L.FLOORPLANS_PDF,100],[L.FLOORPLANS_IMAGE,110],[L.FLOORPLANS_SATELLITE,130],
  [L.OBSTACLES_PIPES,200],[L.OBSTACLES_VENTILATION,210],[L.OBSTACLES_PARAMETRIC_WALLS,220],[L.OBJECTS_DEFAULT,300]]);
export function commonPlanBounds(points) {
  return {minX:Math.min(...points.map(p=>p.x)),minZ:Math.min(...points.map(p=>p.z)),maxX:Math.max(...points.map(p=>p.x)),maxZ:Math.max(...points.map(p=>p.z))};
}
function rectangle(x,z,width,depth,rotation=0) {
  const c=Math.cos(rotation*Math.PI/180),s=Math.sin(rotation*Math.PI/180);
  return [[-1,-1],[1,-1],[1,1],[-1,1]].map(([u,v])=>({x:x+c*u*width/2-s*v*depth/2,z:z+s*u*width/2+c*v*depth/2}));
}
function clip(points,origin,dir,offset,side) {
  const distance=p=>side*((p.x-origin.x)*dir.x+(p.z-origin.z)*dir.z-offset),out=[];
  for(let i=0;i<points.length;i++){
    const a=points[i],b=points[(i+1)%points.length],da=distance(a),db=distance(b);
    if(da>=0)out.push(copyPoint(a));
    if((da>=0)!==(db>=0)){const t=da/(da-db);out.push({x:a.x+(b.x-a.x)*t,z:a.z+(b.z-a.z)*t});}
  }
  return out;
}
/** Detached, read-only common content. Unsupported visible data vetoes the whole GPU view. */
export function prepareCommonPlanContent(project, {pipeSegments,ductSegments,nativeWallOverlays=false,resolveWallColor=wall=>wall.wallColor||'#c2c6ca'}={}) {
  const records=[],fallbacks=[];
  for(const values of [project?.obstacles,project?.objectAssets,project?.sketchWalls?.walls,project?.sketchWalls?.nodes])
    if(values!=null&&(!Array.isArray(values)||values.length>20000))throw new RangeError('Common model collection budget');
  const reject=(kind,id,reason)=>fallbacks.push({kind,id,reason});
  const add=(kind,id,layer,points,style={},extra={})=>{
    if(records.length>=20000)throw new RangeError('Common plan record budget exceeded');
    if(!points?.length||!points.every(validPoint))throw new TypeError('Invalid common geometry');
    records.push({owner:COMMON_PLAN_OWNER,kind,id,layer,order:orders.get(layer),points:points.map(copyPoint),bounds:commonPlanBounds(points),
      fill:'#d9e2ec',stroke:'#263843',strokeWidth:6,alpha:1,locked:false,...style,...extra});
  };
  const fp=project?.floorplan;
  if(fp&&fp.visible!==false){
    const layer=floorplanPlanLayerId(fp);
    if(isPlanLayerVisible(project,layer)){
      if(!orders.has(layer)||!fp.url||![fp.x,fp.z].every(Number.isFinite)||!positive(fp.width)||!positive(fp.height))reject('floorplan','floorplan','Unsupported or incomplete floorplan; stored raster required');
      // PDF rotation is already baked into the stored image by the importer.
      else add('floorplan','floorplan',layer,rectangle(fp.x+fp.width/2,fp.z+fp.height/2,fp.width,fp.height),
        {alpha:Math.max(0,Math.min(1,fp.opacity??0.85)),locked:Boolean(fp.locked||layer===L.FLOORPLANS_SATELLITE)},
        {image:{source:fp.url,format:'raster'},stroke:null});
    }
  }
  const obstacles=Array.isArray(project?.obstacles)?project.obstacles:[];
  for(const type of ['pipe','duct']){
    const layer=type==='pipe'?L.OBSTACLES_PIPES:L.OBSTACLES_VENTILATION;
    if(!isPlanLayerVisible(project,layer))continue;
    const source=obstacles.filter(o=>o.type===type&&o.visible!==false);
    const valid=source.filter(o=>o.id&&validPoint(o.start)&&validPoint(o.end)&&positive(type==='pipe'?o.diameter:o.width));
    for(const o of source)if(!valid.includes(o))reject('obstacle',o.id,'Invalid obstacle geometry');
    // Native network builders are injected: the read contract has no application-state dependency.
    const builder=type==='pipe'?pipeSegments:ductSegments;
    if(valid.length&&!builder){reject('obstacle',type,'Network geometry provider missing');continue;}
    for(const o of builder?builder(valid):[]){
      const dx=o.end.x-o.start.x,dz=o.end.z-o.start.z,len=Math.hypot(dx,dz),width=type==='pipe'?o.diameter:o.width;
      if(!positive(len)){reject('obstacle',o.id,'Degenerate obstacle');continue;}
      const ids=o.sourceObstacleIds||o.sourcePipeIds||[o.id];
      const points=o.__miterPolygons?.body||rectangle((o.start.x+o.end.x)/2,(o.start.z+o.end.z)/2,len,width,Math.atan2(dz,dx)*180/Math.PI);
      add('obstacle',ids[0],layer,points,{fill:type==='pipe'?'#8f9da9':'#aebdca'},
        {sourceIds:[...ids],round:type==='pipe'?{start:copyPoint(o.start),end:copyPoint(o.end),radius:width/2}:null});
    }
  }
  for(const o of obstacles)if(!['pipe','duct'].includes(o.type)&&o.visible!==false
    &&isPlanLayerVisible(project,L.OBSTACLES_VENTILATION))reject('obstacle',o.id,'Unsupported obstacle type');
  if(isPlanLayerVisible(project,L.OBSTACLES_PARAMETRIC_WALLS)){
    const context=createSketchWallGeometryContext(project?.sketchWalls);
    for(const wall of context.allWalls()){
      if(wall.visible===false)continue;
      const a=context.nodeById(wall.startNodeId),b=context.nodeById(wall.endNodeId);
      if(!wall.id||!validPoint(a)||!validPoint(b)||!positive(wall.thickness)||(wall.furring?.enabled&&!nativeWallOverlays)){
        reject('room-wall',wall.id,wall.furring?.enabled?'Furring requires SVG compatibility view':'Invalid room-wall geometry');continue;
      }
      const length=Math.hypot(b.x-a.x,b.z-a.z);
      if(!positive(length)){reject('room-wall',wall.id,'Degenerate room wall');continue;}
      const dir={x:(b.x-a.x)/length,z:(b.z-a.z)/length},corners=context.corners(wall);
      const polygon=[corners.startInner,corners.endInner,corners.endOuter,corners.startOuter];
      const openings=wall.openings||[];
      if(!Array.isArray(openings)||openings.length>1000){reject('room-wall',wall.id,'Opening budget exceeded');continue;}
      if(openings.some(o=>!Number.isFinite(o.offset)||!positive(o.width))){reject('room-wall',wall.id,'Invalid opening');continue;}
      const gaps=[];
      for(const o of [...openings].sort((x,y)=>(x.offset-x.width/2)-(y.offset-y.width/2))){
        const lo=Math.max(0,o.offset-o.width/2),hi=Math.min(length,o.offset+o.width/2);
        if(hi<=lo)continue;
        if(gaps.length&&lo<=gaps.at(-1)[1])gaps.at(-1)[1]=Math.max(hi,gaps.at(-1)[1]);else gaps.push([lo,hi]);
      }
      let start=0;
      for(const [lo,hi] of [...gaps,[length,length]]){
        if(lo>start){let part=polygon;if(start>0)part=clip(part,a,dir,start,1);if(lo<length)part=clip(part,a,dir,lo,-1);
          if(part.length>=3)add('room-wall',wall.id,L.OBSTACLES_PARAMETRIC_WALLS,part,{fill:resolveWallColor(wall)||'#c2c6ca',fillAlpha:0.38,strokeWidth:3,stroke:wall.fixed?'#16a34a':wall.free===false?'#111827':'#dc2626'});}
        start=hi;
      }
    }
  }
  if(isPlanLayerVisible(project,L.OBJECTS_DEFAULT))for(const item of project?.objectAssets||[]){
    if(item.visible===false||item.hidden===true)continue;
    const width=item.width||item.sourceWidth,depth=item.depth||item.sourceDepth;
    if(!item.id||![item.x,item.z,item.rotation??0].every(Number.isFinite)||!positive(width)||!positive(depth)){reject('object',item.id,'Invalid object placement');continue;}
    add('object',item.id,L.OBJECTS_DEFAULT,rectangle(item.x,item.z,width,depth,item.rotation||0),
      {fill:item.color||'#d9e2ec',alpha:item.svgPath?0.78:1,locked:Boolean(item.locked)},
      item.svgPath?{image:{source:item.svgPath,format:'svg'},stroke:null}:{});
  }
  records.sort((a,b)=>a.order-b.order);
  return {records,fallbacks,eligible:fallbacks.length===0};
}
export function commonPointInPolygon(point,points) {
  let inside=false;
  for(let i=0,j=points.length-1;i<points.length;j=i++){
    const a=points[i],b=points[j];
    if((a.z>point.z)!==(b.z>point.z)&&point.x<(b.x-a.x)*(point.z-a.z)/(b.z-a.z)+a.x)inside=!inside;
  }
  return inside;
}
export function hitCommonPlanContent(view,point,{imageHit=()=>true}={}) {
  for(let i=view.records.length-1;i>=0;i--){
    const r=view.records[i];if(r.locked||r.alpha<=0)continue;
    const rounded=r.round&&[r.round.start,r.round.end].some(p=>Math.hypot(point.x-p.x,point.z-p.z)<=r.round.radius);
    if((rounded||commonPointInPolygon(point,r.points))&&(!r.image||imageHit(r,point)))return {owner:COMMON_PLAN_OWNER,kind:r.kind,objectId:r.id,id:r.id,sourceIds:r.sourceIds||[r.id]};
  }
  return null;
}
